import { expenseListQuerySchema, type ExpenseListQuery, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { ApiError } from '../../../shared/shared.api';
import { listExpenses, type ExpenseView } from '../expenses.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ExpensesState =
  { status: 'loading' } | { status: 'loaded'; page: Page<ExpenseView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: ExpenseListQuery) => toListParams(query).toString();

function toErrorState(err: unknown): ExpensesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/**
 * The page of expenses the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. `fetchPage` is the
 * approvals tab's own call (the queue); it must be a stable function.
 */
export function useExpenseList(fetchPage: (query: ExpenseListQuery) => Promise<Page<ExpenseView>> = listExpenses) {
  const list = useListQuery(expenseListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ExpensesState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    fetchPage(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query, fetchPage]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', page: await fetchPage(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, reload };
}
